// Pestaña Producto del detalle del Admin VL: equipo vendido, precios y dispositivo, como Mis ventas.
import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { formatMXN } from '@/shared/lib/format/money';
import { hasEquipment } from '@/shared/patterns/SaleTicket';
import type { SaleSlots } from '@/shared/patterns/SaleReceipt';
import {
    DetailRow,
    PendingDetailRow,
    SaleLinesSection,
    SoldEquipmentSection,
} from '@/domains/sales/portal/mysales/components/SaleDetailSections';
import {
    CREDIT_PRICE_NOTE,
    PENDING_CELL,
    showsSaleLines,
} from '@/domains/sales/portal/mysales/lib/mySalesFormat';
import { saleCategoryLabel } from '@/domains/sales/portal/mysales/lib/mySalesCatalog';
import { ventaDetailFacts } from '../../lib/ventaDetailFacts';
import { TAB_BODY, TabNote, TabSection } from './VentaTab';

/** El Admin VL no lee el expediente del candado: `GET /sales/{id}/device` le responde 403. */
export const DEVICE_NO_ACCESS_NOTE =
    'Tu rol todavía no puede leer el expediente del candado remoto de esta venta.';

export const MARGIN_NO_SOURCE_NOTE =
    'La venta no guarda el costo del equipo, así que no hay margen.';

/** Sin catálogo de tienda: la categoría sale de la línea congelada de la venta. */
const NO_CATALOG = { items: undefined, isLoading: false, isError: false };

export function ProductoTab({
    sale,
    slots = {},
}: {
    sale: BackofficeSale;
    /** Piezas de Promociones: promo por línea, nombre del regalo y «Se lleva». */
    slots?: SaleSlots;
}) {
    const facts = ventaDetailFacts(sale);
    // Sin teléfono no hay equipo, precio de equipo ni IMEI: solo sus productos.
    if (!hasEquipment(sale)) {
        return (
            <div className={TAB_BODY}>
                <SaleLinesSection sale={facts} slots={slots} />
            </div>
        );
    }
    const category = saleCategoryLabel(sale.sku, sale.lines, NO_CATALOG);
    return (
        <div className={TAB_BODY}>
            {showsSaleLines(sale) ? <SaleLinesSection sale={facts} slots={slots} /> : null}
            <SoldEquipmentSection sale={facts}>
                <DetailRow label="Categoría" value={category ?? PENDING_CELL} />
            </SoldEquipmentSection>

            <TabSection title="Precios">
                <DetailRow
                    label="Precio contado"
                    value={
                        sale.equipmentPriceCents === null
                            ? PENDING_CELL
                            : formatMXN(sale.equipmentPriceCents / 100)
                    }
                />
                <PendingDetailRow label="Precio crédito" reason={CREDIT_PRICE_NOTE} />
                <PendingDetailRow label="Margen" reason={MARGIN_NO_SOURCE_NOTE} />
            </TabSection>

            <TabSection title="Dispositivo (IMEI)">
                <div className="rounded-md border border-border p-2">
                    {sale.imei ? (
                        <>
                            <DetailRow
                                label="IMEI"
                                value={<span className="font-mono">{sale.imei}</span>}
                            />
                            <PendingDetailRow label="MDM candado" reason={DEVICE_NO_ACCESS_NOTE} />
                            <PendingDetailRow
                                label="Proveedor MDM"
                                reason={DEVICE_NO_ACCESS_NOTE}
                            />
                        </>
                    ) : (
                        <p className="text-[11px] leading-relaxed text-muted-foreground">
                            Esta venta no registró IMEI. Sin IMEI no hay equipo identificado que
                            ligar al candado remoto.
                        </p>
                    )}
                </div>
            </TabSection>

            {sale.type === 'CREDIT' ? (
                <TabNote>
                    Todo equipo vendido con crédito SureKeep queda registrado con IMEI y candado
                    remoto.
                </TabNote>
            ) : null}
        </div>
    );
}
